import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { timerStore } from '../stores/timer-store';
import { TimerCard } from '../components/timer-card/timer-card';
import { StopwatchCard } from '../components/stopwatch-card/stopwatch-card';
import './timer-ui.scss';

export const TimerUI: React.FC = observer(() => {
  const [activeTab, setActiveTab] = useState<'timers' | 'stopwatches'>('timers');
  const [showCreateTimer, setShowCreateTimer] = useState(false);
  const [showCreateStopwatch, setShowCreateStopwatch] = useState(false);
  const [timerName, setTimerName] = useState('');
  const [timerMinutes, setTimerMinutes] = useState('');
  const [timerSeconds, setTimerSeconds] = useState('');
  const [stopwatchName, setStopwatchName] = useState('');

  const handleCreateTimer = (e: React.FormEvent) => {
    e.preventDefault();
    const minutes = parseInt(timerMinutes) || 0;
    const seconds = parseInt(timerSeconds) || 0;
    const totalSeconds = (minutes * 60) + seconds;
    
    if (timerName.trim() && totalSeconds > 0) {
      timerStore.createTimer(timerName.trim(), totalSeconds);
      setTimerName('');
      setTimerMinutes('');
      setTimerSeconds('');
      setShowCreateTimer(false);
    }
  };

  const handleCreateStopwatch = (e: React.FormEvent) => {
    e.preventDefault();
    if (stopwatchName.trim()) {
      timerStore.createStopwatch(stopwatchName.trim());
      setStopwatchName('');
      setShowCreateStopwatch(false);
    }
  };

  const presetTimers = [
    { name: 'Pomodoro', minutes: 25 },
    { name: 'Short Break', minutes: 5 },
    { name: 'Long Break', minutes: 15 },
    { name: 'Tea Timer', minutes: 3 },
  ];

  const handlePresetTimer = (name: string, minutes: number) => {
    timerStore.createTimer(name, minutes * 60);
  };

  return (
    <div className="timer-ui">
      <div className="timer-ui__header">
        <h1>Timer & Stopwatch</h1>
        <p>Track time with multiple timers and stopwatches</p>
      </div>

      <div className="timer-ui__tabs">
        <button
          onClick={() => setActiveTab('timers')}
          className={`timer-ui__tab ${activeTab === 'timers' ? 'timer-ui__tab--active' : ''}`}
        >
          ⏲️ Timers ({timerStore.allTimers.length})
        </button>
        <button
          onClick={() => setActiveTab('stopwatches')}
          className={`timer-ui__tab ${activeTab === 'stopwatches' ? 'timer-ui__tab--active' : ''}`}
        >
          ⏱️ Stopwatches ({timerStore.allStopwatches.length})
        </button>
      </div>

      {activeTab === 'timers' && (
        <div className="timer-ui__section">
          <div className="timer-ui__controls">
            <button
              onClick={() => setShowCreateTimer(!showCreateTimer)}
              className="timer-ui__add-button"
            >
              + New Timer
            </button>
            
            <div className="timer-ui__presets">
              <span className="timer-ui__presets-label">Quick presets:</span>
              {presetTimers.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => handlePresetTimer(preset.name, preset.minutes)}
                  className="timer-ui__preset-button"
                >
                  {preset.name} ({preset.minutes}m)
                </button>
              ))}
            </div>
          </div>

          {showCreateTimer && (
            <form onSubmit={handleCreateTimer} className="timer-ui__form">
              <h3>Create New Timer</h3>
              <div className="timer-ui__form-group">
                <label htmlFor="timer-name">Timer Name</label>
                <input
                  id="timer-name"
                  type="text"
                  value={timerName}
                  onChange={(e) => setTimerName(e.target.value)}
                  placeholder="e.g., Study Session"
                  required
                />
              </div>
              <div className="timer-ui__form-row">
                <div className="timer-ui__form-group">
                  <label htmlFor="timer-minutes">Minutes</label>
                  <input
                    id="timer-minutes"
                    type="number"
                    min="0"
                    max="999"
                    value={timerMinutes}
                    onChange={(e) => setTimerMinutes(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="timer-ui__form-group">
                  <label htmlFor="timer-seconds">Seconds</label>
                  <input
                    id="timer-seconds"
                    type="number"
                    min="0"
                    max="59"
                    value={timerSeconds}
                    onChange={(e) => setTimerSeconds(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="timer-ui__form-actions">
                <button type="submit" className="timer-ui__submit-button">
                  Create Timer
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateTimer(false)}
                  className="timer-ui__cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="timer-ui__grid">
            {timerStore.allTimers.map(timer => (
              <TimerCard key={timer.id} timer={timer} />
            ))}
            {timerStore.allTimers.length === 0 && (
              <div className="timer-ui__empty">
                <p>No timers yet. Create your first timer to get started!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'stopwatches' && (
        <div className="timer-ui__section">
          <div className="timer-ui__controls">
            <button
              onClick={() => setShowCreateStopwatch(!showCreateStopwatch)}
              className="timer-ui__add-button"
            >
              + New Stopwatch
            </button>
          </div>

          {showCreateStopwatch && (
            <form onSubmit={handleCreateStopwatch} className="timer-ui__form">
              <h3>Create New Stopwatch</h3>
              <div className="timer-ui__form-group">
                <label htmlFor="stopwatch-name">Stopwatch Name</label>
                <input
                  id="stopwatch-name"
                  type="text"
                  value={stopwatchName}
                  onChange={(e) => setStopwatchName(e.target.value)}
                  placeholder="e.g., Running Session"
                  required
                />
              </div>
              <div className="timer-ui__form-actions">
                <button type="submit" className="timer-ui__submit-button">
                  Create Stopwatch
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateStopwatch(false)}
                  className="timer-ui__cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="timer-ui__grid">
            {timerStore.allStopwatches.map(stopwatch => (
              <StopwatchCard key={stopwatch.id} stopwatch={stopwatch} />
            ))}
            {timerStore.allStopwatches.length === 0 && (
              <div className="timer-ui__empty">
                <p>No stopwatches yet. Create your first stopwatch to get started!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
